import { RD_BASE } from './config';

// ── Dune Sim：链上实时代币数据 ────────────────────────
// chain_id 映射：1=Ethereum, 56=BSC, 137=Polygon, 42161=Arbitrum, 8453=Base
const CHAIN_IDS = { ethereum: 1, "binance-smart-chain": 56, "polygon-pos": 137, arbitrum: 42161, base: 8453, solana: null };

// CoinGecko platform key → Dune blockchain name
const CG_TO_DUNE_CHAIN = {
  ethereum:             'ethereum',
  'binance-smart-chain': 'bnb',
  'polygon-pos':        'polygon',
  arbitrum:             'arbitrum',
  base:                 'base',
  optimism:             'optimism',
  avalanche:            'avalanche_c',
  hyperevm:             'hyperevm',     // HyperEVM ERC-20 代币（dex.trades 已收录）
  hyperliquid:          'hyperliquid',  // Hyperliquid 原生链（走 hl-analytics 端点）
};

// 非 EVM 公链（有自己原生 chain，CoinGecko platforms 里会出现这些 key）。
// 这些项目在 dex.trades 表里没有覆盖，HL 也不是它们的主要交易场所，
// 走 HL 兜底反而拉出无关数据 + 502 噪音。CoinGecko 全市场 volume 才是正解。
const NON_EVM_L1_KEYS = new Set([
  'sui',
  'aptos',
  'solana',
  'cosmos',
  'near-protocol',
  'tron',
  'ton',
  'cardano',
  'algorand',
  'stellar',
  'flow',
  'tezos',
]);

// 跳过零地址（CoinGecko 对原生代币会用 0x0…0 作占位符）
const ZERO_ADDR = '0x0000000000000000000000000000000000000000';
const isValidContract = (addr) => typeof addr === 'string' && addr.length >= 10 && addr.toLowerCase() !== ZERO_ADDR;

export async function fetchDuneTokenInfo(cgPlatforms) {
  if (!cgPlatforms) return null;
  // 优先 Ethereum，其次 Base / Arbitrum
  const preferred = ["ethereum", "base", "arbitrum", "binance-smart-chain", "polygon-pos"];
  let contract = null, chainId = 1;
  for (const chain of preferred) {
    const addr = cgPlatforms[chain];
    if (isValidContract(addr)) {
      contract = addr;
      chainId  = CHAIN_IDS[chain] || 1;
      break;
    }
  }
  if (!contract) return null;
  try {
    const res = await fetch(`${RD_BASE}/dune/token-info?contract=${encodeURIComponent(contract)}&chain_id=${chainId}`, { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return null;
    const json = await res.json();
    const token = (json.tokens || [])[0];
    if (!token) return null;
    return {
      contract,
      chain: preferred.find(c => cgPlatforms[c] === contract) || "ethereum",
      chainId,
      priceUsd:   token.price_usd   ?? null,
      poolSize:   token.pool_size   ?? null,   // 流动性深度（USD）
      fdv:        token.fully_diluted_value ?? null,
      totalSupply: token.total_supply ? Number(token.total_supply) / Math.pow(10, token.decimals || 18) : null,
      poolType:   token.pool?.pool_type ?? null,
    };
  } catch { return null; }
}

// ── Dune Analytics：30天 DEX 交易数据（dex.trades 表）──────────────────
// EVM 链优先级：hyperevm 排在原生 EVM 链之后（通常 HYPE 在 hyperliquid 原生链）
const PREFERRED_DEX_CHAINS = [
  "ethereum", "base", "arbitrum", "binance-smart-chain", "polygon-pos", "optimism", "hyperevm",
];

// Hyperliquid 原生链：用 hyperliquid.market_data 按 symbol 查询（无 ERC-20 合约）
async function fetchHLNativeAnalytics(coinSymbol) {
  try {
    const res = await fetch(
      `${RD_BASE}/dune/hl-analytics?coin=${encodeURIComponent(coinSymbol.toUpperCase())}`,
      { signal: AbortSignal.timeout(30000) },
    );
    if (!res.ok) return null;
    return await res.json(); // { days, total_volume, chain:'hyperliquid', source:'hyperliquid_market_data' }
  } catch { return null; }
}

export async function fetchDuneAnalytics(cgPlatforms, tokenSymbol = null) {
  if (!cgPlatforms && !tokenSymbol) return null;

  // 1. 优先尝试 EVM 链（dex.trades）
  let contract = null, duneChain = 'ethereum';
  if (cgPlatforms) {
    for (const chain of PREFERRED_DEX_CHAINS) {
      const addr = cgPlatforms[chain];
      // 跳过零地址（原生代币占位符）
      if (addr && addr !== '0x0000000000000000000000000000000000000000') {
        contract  = addr.toLowerCase();
        duneChain = CG_TO_DUNE_CHAIN[chain] || 'ethereum';
        break;
      }
    }
  }

  if (contract) {
    try {
      const res = await fetch(
        `${RD_BASE}/dune/analytics?contract=${encodeURIComponent(contract)}&chain=${encodeURIComponent(duneChain)}`,
        { signal: AbortSignal.timeout(30000) },
      );
      if (res.ok) return await res.json();
    } catch { /* fall through */ }
  }

  // 2. HL-native token（HYPE / PURR）走 hl-analytics 端点
  const isHLNative = cgPlatforms?.['hyperliquid'] != null;
  if (isHLNative && tokenSymbol) {
    return await fetchHLNativeAnalytics(tokenSymbol);
  }

  // 3. 非 EVM 公链（Sui / Aptos / Solana 等）—— 不走 HL 兜底，让上游用 CoinGecko 全市场数据
  // 之前的兜底逻辑会让 Sui 错误命中 HL 永续合约成交量，跟全市场 spot volume 是两码事
  if (cgPlatforms && Object.keys(cgPlatforms).some(k => NON_EVM_L1_KEYS.has(k))) {
    return null;
  }

  // 4. EVM 无合约但有 symbol 的边角场景，最后尝试一次 HL（保留旧行为）
  if (!contract && tokenSymbol) {
    return await fetchHLNativeAnalytics(tokenSymbol);
  }

  return null;
}

// ── Dune Dashboard：自动搜索公开看板 / 生成激活链接 ──────────────────
const PREFERRED_CHAINS = ['ethereum', 'arbitrum', 'base', 'polygon-pos', 'optimism', 'binance-smart-chain', 'avalanche'];

/**
 * 先在 Dune 上搜索项目官方看板（按质量评分），找不到再用合约地址生成激活链接。
 *
 * 返回值类型：
 * - { type:'dashboard', embedUrl, dashboardUrl, owner, slug, score }  ← 自动发现的官方看板
 * - { type:'activate',  queryId, activateUrl, contract, chain }       ← 参数化查询激活链接
 * - { type:'none' }                                                   ← 无数据
 *
 * @param {object} cgPlatforms   CoinGecko platforms 字段（链 → 合约地址）
 * @param {string} [projectName] 项目名，用于搜索官方看板（优先级最高）
 */
export async function findOrPrepareDuneDashboard(cgPlatforms, projectName = '') {
  // 找出首选链 + 合约地址（跳过零地址占位）
  let contract = null;
  let duneChain = 'ethereum';
  for (const cgChain of PREFERRED_CHAINS) {
    const addr = cgPlatforms?.[cgChain];
    if (isValidContract(addr)) {
      contract  = addr.toLowerCase();
      duneChain = CG_TO_DUNE_CHAIN[cgChain] || 'ethereum';
      break;
    }
  }

  // 既无合约也无项目名 → 直接跳过
  if (!contract && !projectName) return { type: 'none' };

  try {
    const params = new URLSearchParams();
    if (projectName) params.set('name', projectName);
    if (contract)    params.set('contract', contract);
    if (contract)    params.set('chain', duneChain);

    const res = await fetch(
      `${RD_BASE}/dune/find-dashboard?${params}`,
      { signal: AbortSignal.timeout(12000) },
    );
    if (!res.ok) return { type: 'none' };
    return await res.json();
  } catch { return { type: 'none' }; }
}

/**
 * 验证并解析用户粘贴的 Dune URL，返回 embed 信息。
 * @param {string} rawUrl
 * @returns {{ ok:true, embedUrl, dashboardUrl, title } | { ok:false }}
 */
export async function parseDuneUrl(rawUrl) {
  if (!rawUrl?.trim()) return { ok: false };
  try {
    const res = await fetch(
      `${RD_BASE}/dune/parse-url?url=${encodeURIComponent(rawUrl.trim())}`,
      { signal: AbortSignal.timeout(5000) },
    );
    if (!res.ok) return { ok: false };
    return await res.json();
  } catch { return { ok: false }; }
}
